import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../contexts/MessageContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';
import { Button } from '../ui/button';
import { employerAPI, employeeAPI } from '../../services/api';
import TrialBanner from '../common/TrialBanner';
import EmailVerificationBanner from '../common/EmailVerificationBanner';
import EmployerHoldingPage from '../../pages/employer/EmployerHoldingPage';
import { 
  Shield, LayoutDashboard, User, FileText, MessageSquare, Settings, LogOut, 
  Building, Search, Bookmark, Menu, X, ClipboardList, BarChart3, Users, Send, Mail, Gift, Star, Share2, Award, GraduationCap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * I3: Feature flag to nav item mapping
 * Maps nav item paths to their corresponding feature flags.
 * Items not in this map are always shown.
 */
const NAV_FEATURE_FLAGS = {
  // Employee nav items
  '/employee/messages': 'messaging',
  '/employee/peer-reviews': 'peer_reviews',
  '/employee/referrals': 'referral_program',
  // Employer nav items  
  '/employer/messages': 'messaging',
  '/employer/team-comparison': 'team_comparison',
  '/employer/pipeline': 'pipeline',
  '/employer/referrals': 'referral_program',
  '/employer/settings/ats': 'ats_integration',
};

const DashboardLayout = ({ children }) => {
  const { user, logout, emailVerified } = useAuth();
  const { unreadCount, refreshUnreadCount } = useMessages();
  const { isFeatureEnabled, loading: flagsLoading } = useFeatureFlags();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [employerPlatformLive, setEmployerPlatformLive] = useState(null); // null = loading, true/false = resolved
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [gatingChecked, setGatingChecked] = useState(false);
  
  const isEmployer = user?.account_type === 'employer';
  const isAdmin = user?.is_admin || user?.is_superadmin;
  const unreadMessages = unreadCount;

  // Refresh unread count on navigation (every page change)
  useEffect(() => {
    refreshUnreadCount();
  }, [location.pathname, refreshUnreadCount]);

  // Poll for new messages every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  // Fetch trial status for employers
  useEffect(() => {
    if (isEmployer) {
      employerAPI.getProfile()
        .then(res => {
          setTrialStatus({
            trialActive: res.data.trial_active,
            trialDaysRemaining: res.data.trial_days_remaining,
            trialEndDate: res.data.trial_end_date,
            subscriptionTier: res.data.subscription_tier
          });
        })
        .catch(err => console.error('Failed to fetch trial status:', err));
    }
    // Refresh unread count on mount
    refreshUnreadCount();
  }, [isEmployer, user, refreshUnreadCount]);

  // A5: Check feature flags and superadmin status for employer gating
  // Uses the already-available useFeatureFlags hook instead of raw fetch
  useEffect(() => {
    if (!isEmployer) {
      setGatingChecked(true);
      return;
    }
    
    // Check if user is superadmin from user object
    setIsSuperadmin(user?.is_superadmin === true);
    
    // Wait for feature flags to load before making gating decision
    if (!flagsLoading) {
      // Use the authenticated feature flag value from useFeatureFlags hook
      const platformLive = isFeatureEnabled('employer_platform_live');
      setEmployerPlatformLive(platformLive);
      setGatingChecked(true);
    }
  }, [isEmployer, user, flagsLoading, isFeatureEnabled]);

  // A5: Show holding page for non-superadmin employers when platform not live
  // Only check after gating has been resolved
  const showHoldingPage = gatingChecked && isEmployer && employerPlatformLive === false && !isSuperadmin;
  
  // Loading state while checking employer gating (used in JSX, not early return)
  const showGatingLoading = isEmployer && !gatingChecked;
  
  const employeeLinks = [
    { href: '/employee/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/employee/profile', label: 'Profile', icon: User },
    { href: '/employee/share', label: 'Share Badge', icon: Share2 },
    { href: '/employee/certificates', label: 'Certificates', icon: Award },
    { href: '/employee/education', label: 'Education', icon: GraduationCap },
    { href: '/employee/references', label: 'References', icon: Users },
    { href: '/employee/peer-reviews', label: 'Peer Reviews', icon: Star },
    { href: '/employee/record-requests', label: 'Request Records', icon: Send },
    { href: '/employee/email-signature', label: 'Email Signature', icon: Mail },
    { href: '/employee/referrals', label: 'Referrals', icon: Gift },
    { href: '/employee/connected', label: 'Connected', icon: Building },
    { href: '/employee/records', label: 'My Records', icon: FileText },
    { href: '/employee/reviews', label: 'Reviews', icon: ClipboardList },
    { href: '/employee/analytics', label: 'My Performance', icon: BarChart3 },
    { href: '/employee/messages', label: 'Messages', icon: MessageSquare },
    { href: '/employee/settings', label: 'Settings', icon: Settings },
  ];
  
  const employerLinks = [
    { href: '/employer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/employer/company', label: 'Company', icon: Building },
    { href: '/employer/search', label: 'Search Talent', icon: Search },
    { href: '/employer/pipeline', label: 'Pipeline', icon: Users },
    { href: '/employer/saved', label: 'Saved Candidates', icon: Bookmark },
    { href: '/employer/records', label: 'Records', icon: FileText },
    { href: '/employer/reviews', label: 'Reviews', icon: ClipboardList },
    { href: '/employer/analytics', label: 'Team Analytics', icon: BarChart3 },
    { href: '/employer/team-comparison', label: 'Team Comparison', icon: Star },
    { href: '/employer/referrals', label: 'Referrals', icon: Gift },
    { href: '/employer/messages', label: 'Messages', icon: MessageSquare },
    { href: '/employer/settings', label: 'Settings', icon: Settings },
  ];
  
  // I3: Filter nav links based on feature flags
  // Admins see all items regardless of flag state (admin bypass)
  const baseLinks = isEmployer ? employerLinks : employeeLinks;
  const links = baseLinks.filter(link => {
    const flagKey = NAV_FEATURE_FLAGS[link.href];
    
    // No flag restriction - always show
    if (!flagKey) {
      return true;
    }
    
    // Admin bypass: admins see all nav items regardless of flag state
    if (isAdmin) {
      return true;
    }
    
    // While flags are loading, hide flagged items (fail-closed)
    if (flagsLoading) {
      return false;
    }
    
    // Check if feature is enabled for this user
    return isFeatureEnabled(flagKey);
  });
  
  const handleLogout = () => {
    logout();
  };

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Calculate if we should show trial banner (fixed at top for employers)
  const showTrialBanner = isEmployer && trialStatus && (
    trialStatus.trialActive || 
    (trialStatus.subscriptionTier === 'free' && !trialStatus.trialActive)
  );

  // A5: Show holding page for non-superadmin employers when platform not live
  if (showHoldingPage) {
    return <EmployerHoldingPage />;
  }

  // A5: Show loading while checking employer gating
  if (showGatingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-lg flex items-center justify-center" style={{ background: '#1B5AFF' }}>
            <Shield className="h-6 w-6 text-[#FFFFFF] animate-pulse" />
          </div>
          <p style={{ color: '#5B6472' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Fixed Trial Banner - Always visible for employers */}
      {showTrialBanner && (
        <TrialBanner 
          trialActive={trialStatus.trialActive}
          trialDaysRemaining={trialStatus.trialDaysRemaining}
          trialEndDate={trialStatus.trialEndDate}
          isFixed={true}
        />
      )}
      
      {/* Email Verification Banner - Fixed below trial banner */}
      {!emailVerified && (
        <div 
          className="fixed left-0 right-0 z-[55] lg:left-64"
          style={{ top: showTrialBanner ? '44px' : '0px' }}
        >
          <EmailVerificationBanner />
        </div>
      )}

      {/* Mobile Header - Fixed position */}
      <header 
        className="lg:hidden fixed left-0 right-0 h-16 bg-[#FFFFFF] border-b border-[#E8EDF5] z-40 flex items-center justify-between px-4"
        style={{ 
          top: showTrialBanner && !emailVerified ? '88px' : 
               showTrialBanner ? '44px' : 
               !emailVerified ? '44px' : '0px' 
        }}
      >
        <Link to="/" className="flex items-center gap-2">
          <Shield className="h-7 w-7 text-[#1B5AFF]" />
          <span className="text-lg font-bold text-[#0A0F1E]">VETT</span>
        </Link>
        <div className="flex items-center gap-2">
          {/* Logout button - always visible on mobile */}
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg text-[#5B6472] hover:text-[#1B5AFF] hover:bg-[#F5F8FF] transition-colors"
            data-testid="mobile-logout-btn"
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
          {/* Messages shortcut with badge */}
          <Link
            to={isEmployer ? '/employer/messages' : '/employee/messages'}
            className="relative p-2 rounded-lg text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF] transition-colors"
            data-testid="mobile-messages-btn"
          >
            <MessageSquare className="h-6 w-6" />
            {unreadMessages > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] text-[10px] font-bold shadow-lg shadow-[#1B5AFF]/50 animate-pulse">
                {unreadMessages > 9 ? '9+' : unreadMessages}
              </span>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF] transition-colors"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Mobile: full height overlay with scroll */}
      <aside className={`
        fixed left-0 w-64 bg-[#FFFFFF] border-r border-[#E8EDF5] z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        lg:top-0 lg:h-screen
      `} style={{ 
        top: sidebarOpen ? '0' : (showTrialBanner ? '44px' : '0'),
        height: sidebarOpen ? '100vh' : (showTrialBanner ? 'calc(100vh - 44px)' : '100vh')
      }}>
        {/* Logo */}
        <div className="px-6 py-4 border-b border-[#E8EDF5] flex-shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="h-8 w-8 text-[#1B5AFF]" />
            <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
          </Link>
        </div>
        
        {/* Scrollable Navigation Container - takes remaining height */}
        <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100% - 65px)' }}>
          {/* Navigation - scrollable */}
          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            {links.map((link) => {
              const isActive = location.pathname === link.href || 
                (link.href !== '/employee/dashboard' && link.href !== '/employer/dashboard' && location.pathname.startsWith(link.href));
              const isMessages = link.label === 'Messages';
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all relative ${
                    isActive
                      ? 'bg-[#1B5AFF]/10 text-[#1B5AFF] border border-[#1B5AFF]/30'
                      : 'text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="relative">
                    <link.icon className="h-5 w-5 flex-shrink-0" />
                    {/* Red dot notification on icon */}
                    {isMessages && unreadMessages > 0 && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#1B5AFF] rounded-full animate-pulse" />
                    )}
                  </div>
                  <span className="font-medium flex-1 text-sm">{link.label}</span>
                  {/* Badge with count */}
                  {isMessages && unreadMessages > 0 && (
                    <span 
                      className="px-2 py-0.5 rounded-full bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] text-xs font-bold min-w-[22px] text-center shadow-lg shadow-[#1B5AFF]/50 animate-pulse" 
                      data-testid="unread-messages-badge"
                    >
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          
          {/* User Info & Logout - Fixed at bottom */}
          <div className="p-3 border-t border-[#E8EDF5] flex-shrink-0 bg-[#FFFFFF]">
            <div className="flex items-center gap-2 mb-3 px-2">
              <div className="w-8 h-8 rounded-full bg-[#1B5AFF]/20 flex items-center justify-center flex-shrink-0">
                {isEmployer ? (
                  <Building className="h-4 w-4 text-[#1B5AFF]" />
                ) : (
                  <User className="h-4 w-4 text-[#1B5AFF]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-[#0A0F1E] truncate">{user?.email}</div>
                <div className="text-[10px] text-[#5B6472] capitalize">{user?.account_type}</div>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-lg py-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </Button>
          </div>
        </div>
      </aside>
      
      {/* Main Content - with proper top padding for fixed header */}
      <main className="lg:ml-64 min-h-screen" style={{
        paddingTop: `${
          (showTrialBanner ? 44 : 0) + 
          (!emailVerified ? 44 : 0) + 
          64 // mobile header height
        }px`
      }}>
        <div className="lg:pt-0 p-4 sm:p-6 lg:p-8" style={{
          paddingTop: window.innerWidth >= 1024 ? (showTrialBanner ? '44px' : '0') : undefined
        }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
